import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { EditorSelection } from '@peruselab/structure-editor/domain/editor';
import { exportSelection } from '@peruselab/structure-editor/domain/exportScope';
import { pageOf } from '@peruselab/structure-editor/domain/page';
import { pageRect } from '@peruselab/structure-editor/domain/pageExtent';
import { MM_PER_PX, type SvgRenderOptions } from '@peruselab/structure-editor/rendering/svgExport';
import {
  loadExportFonts,
  requiredExportFonts,
  textExportReport,
  type ExportFonts,
  type TextExportReport
} from '@peruselab/structure-editor/rendering/textOutlines';
import { type ExportSettings, pixelSize } from './exportModel';

// #758 -- everything the dialog needs from the workspace: the drawing, the current selection, and a
// renderer bound to the workspace's own bond/stereo/Markush inputs (the same one Export SVG used).
export interface ExportSource {
  readonly document: DrawingDocument;
  readonly selection: EditorSelection;
  readonly renderSvg: (options: SvgRenderOptions) => string;
}

export interface PreparedExport {
  readonly svg: string;
  // The artwork's own size plus padding, before any chosen output size.
  readonly naturalMm: { readonly width: number; readonly height: number };
  readonly sizeMm: { readonly width: number; readonly height: number };
  readonly text: TextExportReport | null;
  // Fonts could not load: text stays editable (reported, not blocking).
  readonly fontsUnavailable: boolean;
  readonly selectionEmpty: boolean;
  readonly drawingEmpty: boolean;
  // Page scope: some of the drawing lies beyond the page and is cut off (shown, not blocking).
  readonly clipped: boolean;
}

// Font loading is shared across previews; a failed load is retried next time.
let fontCache: { readonly key: string; readonly fonts: Promise<ExportFonts> } | null = null;
function fontsFor(document: DrawingDocument): Promise<ExportFonts> | null {
  const keys = [...requiredExportFonts(document)].sort();
  if (keys.length === 0) return null;
  const key = keys.join(',');
  if (fontCache?.key !== key) {
    const fonts = loadExportFonts(keys, `${import.meta.env.BASE_URL}fonts/export`);
    fonts.catch(() => {
      if (fontCache?.key === key) fontCache = null;
    });
    fontCache = { key, fonts };
  }
  return fontCache.fonts;
}

const mmAttribute = (svg: string, name: 'width' | 'height') =>
  Number(new RegExp(` ${name}="([\\d.]+)mm"`).exec(svg)?.[1] ?? Number.NaN);

function viewBoxOf(svg: string) {
  const [x = 0, y = 0, width = 0, height = 0] = (/ viewBox="([^"]+)"/.exec(svg)?.[1] ?? '').split(' ').map(Number);
  return { x, y, width, height };
}

// Half a unit of slack: extents are rounded to one decimal in the SVG.
const CLIP_TOLERANCE = 0.5;

export async function prepareExport(source: ExportSource, settings: ExportSettings): Promise<PreparedExport> {
  const { document } = source;
  const scope = exportSelection(document, source.selection);
  const drawingEmpty = document.structure.graph.nodes.length === 0 && document.annotations.length === 0;

  let textOutlines: ExportFonts | undefined;
  let text: TextExportReport | null = null;
  let fontsUnavailable = false;
  const pending = fontsFor(document);
  if (pending) {
    try {
      textOutlines = await pending;
      text = textExportReport(document, textOutlines);
    } catch {
      fontsUnavailable = true;
    }
  }

  // #805: the page as grown to hold the content (the document carries its current extent).
  const page = pageRect(pageOf(document));
  const base: SvgRenderOptions = {
    geometry: 'authored',
    ...(settings.scope === 'page'
      ? { pageBounds: page }
      : { paddingPx: settings.paddingMm / MM_PER_PX }),
    physicalSize: true,
    background: settings.background.kind === 'transparent' ? null : settings.background.color,
    ...(textOutlines ? { textOutlines } : {}),
    ...(settings.scope === 'selection'
      ? { include: { atomIds: scope.atomIds, annotationIds: scope.annotationIds } }
      : {})
  };
  const natural = source.renderSvg(base);
  const naturalMm = { width: mmAttribute(natural, 'width'), height: mmAttribute(natural, 'height') };
  const sizeMm = settings.sizeMm ?? naturalMm;
  const svg = settings.sizeMm ? source.renderSvg({ ...base, sizeMm: settings.sizeMm }) : natural;
  let clipped = false;
  if (settings.scope === 'page' && !drawingEmpty) {
    const { pageBounds: _page, ...rest } = base;
    const extents = viewBoxOf(source.renderSvg({ ...rest, paddingPx: 0, background: null }));
    clipped =
      extents.x < page.x - CLIP_TOLERANCE ||
      extents.y < page.y - CLIP_TOLERANCE ||
      extents.x + extents.width > page.x + page.width + CLIP_TOLERANCE ||
      extents.y + extents.height > page.y + page.height + CLIP_TOLERANCE;
  }
  return {
    svg,
    naturalMm,
    sizeMm,
    text,
    fontsUnavailable,
    selectionEmpty: scope.empty,
    drawingEmpty,
    clipped
  };
}

// PNG/JPEG: the exact SVG the preview shows, drawn onto a canvas at the chosen DPI.
export async function rasterize(
  svg: string,
  sizeMm: { readonly width: number; readonly height: number },
  dpi: number,
  format: 'png' | 'jpeg'
): Promise<Blob> {
  const pixels = pixelSize(sizeMm, dpi);
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = pixels.width;
    canvas.height = pixels.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    context.drawImage(image, 0, 0, pixels.width, pixels.height);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be encoded.'))),
        format === 'png' ? 'image/png' : 'image/jpeg',
        0.92
      )
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
